import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Container,
  Tooltip
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  TrendingDown as TrendingDownIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../utils/permissions';
import { usageProductConfig } from '../../../inventory/inventory-components/Usage/usageConfigs/usageProductConfig';

const InventoryUsageSection = ({ onSave }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId, id: poId } = useParams();
  const { canViewInventoryUsage, canEditInventoryUsage } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [deducting, setDeducting] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [piInfo, setPiInfo] = useState({ piNumber: '', customerName: '', companyName: '' });
  const [inventoryDeducted, setInventoryDeducted] = useState(false);
  const [inventoryDeductedAt, setInventoryDeductedAt] = useState(null);
  const [inventoryDeductedBy, setInventoryDeductedBy] = useState(null);
  const [inventorySkipped, setInventorySkipped] = useState(false);
  const [inventorySkippedAt, setInventorySkippedAt] = useState(null);
  const [inventorySkippedBy, setInventorySkippedBy] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [reconfirmDialogOpen, setReconfirmDialogOpen] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deductionNotes, setDeductionNotes] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [inventoryLevels, setInventoryLevels] = useState({});
  const [hasInsufficientStock, setHasInsufficientStock] = useState(false);

  const canView = canViewInventoryUsage();
  const canEdit = canEditInventoryUsage();

  useEffect(() => {
    if (poId && poId !== 'new') {
      loadData(true);
    }
  }, [poId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && poId && poId !== 'new') {
        loadData(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [poId]);

  useEffect(() => {
    if (lineItems.length > 0 && Object.keys(inventoryLevels).length > 0) {
      let hasInsufficient = false;
      for (const item of lineItems) {
        const needed = calculateTotalUnits(item);
        const available = inventoryLevels[item.id] || 0;
        if (needed > available) { hasInsufficient = true; break; }
      }
      setHasInsufficientStock(hasInsufficient);
    }
  }, [lineItems, inventoryLevels]);

  const loadData = async (forceClearCache = false) => {
    if (forceClearCache) {
      setInventoryLevels({});
    }
    setLoading(true);
    try {
      // Fetch PI number + customer name
      const { data: poData } = await supabase
        .from('customer_purchase_orders')
        .select('po_number, customer_name, customer:customer_id(company)')
        .eq('id', poId)
        .maybeSingle();
      if (poData) {
        setPiInfo({
          piNumber:    poData.po_number     || '',
          customerName: poData.customer_name || '',
          companyName:  poData.customer?.company || '',
        });
      }

      // Get latest po_creation record
      const { data: poCreationData, error: poCreationError } = await supabase
        .from('po_creation')
        .select('id')
        .eq('purchase_order_id', poId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (poCreationError) throw poCreationError;

      if (!poCreationData) {
        setLineItems([]);
        setLoading(false);
        return;
      }

      // Fetch active line items
      const { data: items, error: itemsError } = await supabase
        .from('po_creation_items')
        .select(`
          *,
          product:product_id(id, name, sku),
          variant:variant_id(id, name, sku),
          location:location_id(id, name)
        `)
        .eq('po_creation_id', poCreationData.id)
        .eq('line_status', 'active')
        .order('line_order');

      if (itemsError) throw itemsError;

      // Enrich items with unit config + inventory_item_id from product_inventory_items
      const enrichedItems = await Promise.all((items || []).map(async (item) => {
        if (!item.product_id || !item.location_id) {
          return { ...item, unitConfig: null };
        }
        try {
          let unitConfig = null;
          let foundInventoryItemId = item.inventory_item_id;

          // Priority 1: look up by lot/batch (includes inactive for ID lookup)
          if (item.lot_number || item.batch_number) {
            let q = supabase
              .from('product_inventory_items')
              .select('id, units_per_case, cases_per_pallet, enable_cases, enable_pallets')
              .eq('company_id', companyId)
              .eq('product_id', item.product_id)
              .eq('location_id', item.location_id);

            if (item.variant_id) q = q.eq('variant_id', item.variant_id);
            else q = q.is('variant_id', null);
            if (item.lot_number) q = q.eq('lot_number', String(item.lot_number));
            if (item.batch_number) q = q.eq('batch_number', String(item.batch_number));

            const { data: inv } = await q.maybeSingle();
            if (inv) {
              unitConfig = {
                units_per_case: inv.units_per_case || 1,
                cases_per_pallet: inv.cases_per_pallet || 1,
                enable_cases: inv.enable_cases !== false,
                enable_pallets: inv.enable_pallets === true,
              };
              if (!foundInventoryItemId) foundInventoryItemId = inv.id;
            }
          }

          // Priority 2: any active lot for this product/location
          if (!unitConfig) {
            let q = supabase
              .from('product_inventory_items')
              .select('id, units_per_case, cases_per_pallet, enable_cases, enable_pallets')
              .eq('company_id', companyId)
              .eq('product_id', item.product_id)
              .eq('location_id', item.location_id)
              .eq('is_active', true)
              .gt('total_base_units', 0)
              .limit(1);

            if (item.variant_id) q = q.eq('variant_id', item.variant_id);
            else q = q.is('variant_id', null);

            const { data: inv } = await q.maybeSingle();
            if (inv) {
              unitConfig = {
                units_per_case: inv.units_per_case || 1,
                cases_per_pallet: inv.cases_per_pallet || 1,
                enable_cases: inv.enable_cases !== false,
                enable_pallets: inv.enable_pallets === true,
              };
              if (!foundInventoryItemId) foundInventoryItemId = inv.id;
            }
          }

          // Fallback to stored values
          if (!unitConfig) {
            unitConfig = {
              units_per_case: item.units_per_case || 1,
              cases_per_pallet: item.cases_per_pallet || 1,
              enable_cases: (item.units_per_case || 1) > 1,
              enable_pallets: false,
            };
          }

          return { ...item, unitConfig, inventory_item_id: foundInventoryItemId };
        } catch (err) {
          console.error(`Error loading unit config for ${item.product_name}:`, err);
          return {
            ...item,
            unitConfig: { units_per_case: 1, cases_per_pallet: 1, enable_cases: false, enable_pallets: false },
          };
        }
      }));

      setLineItems(enrichedItems);

      // Load inventory levels from product_inventory_items (same source as ProductTable)
      const levels = {};
      for (const item of enrichedItems) {
        if (!item.product_id || !item.location_id) { levels[item.id] = 0; continue; }
        try {
          if (item.lot_number || item.batch_number) {
            let q = supabase
              .from('product_inventory_items')
              .select('total_base_units')
              .eq('company_id', companyId)
              .eq('product_id', item.product_id)
              .eq('location_id', item.location_id)
              .eq('is_active', true);

            if (item.variant_id) q = q.eq('variant_id', item.variant_id);
            else q = q.is('variant_id', null);
            if (item.lot_number) q = q.eq('lot_number', String(item.lot_number));
            if (item.batch_number) q = q.eq('batch_number', String(item.batch_number));

            const { data: lotItems } = await q;
            levels[item.id] = (lotItems || []).reduce((sum, l) => sum + (l.total_base_units || 0), 0);
          } else if (item.inventory_item_id) {
            const { data: inv } = await supabase
              .from('product_inventory_items')
              .select('total_base_units')
              .eq('id', item.inventory_item_id)
              .single();
            levels[item.id] = inv?.total_base_units || 0;
          } else {
            const { data: agg } = await supabase
              .from('product_inventory')
              .select('total_units')
              .eq('company_id', companyId)
              .eq('product_id', item.product_id)
              .eq('variant_id', item.variant_id || null)
              .eq('location_id', item.location_id)
              .maybeSingle();
            levels[item.id] = agg?.total_units || 0;
          }
        } catch {
          levels[item.id] = 0;
        }
      }
      setInventoryLevels(levels);

      // Load deduction/skip status
      const { data: confirmation, error: confError } = await supabase
        .from('po_delivery_confirmations')
        .select('inventory_deducted, inventory_deducted_at, inventory_deducted_by, inventory_deduction_notes, inventory_skipped, inventory_skipped_at, inventory_skipped_by, inventory_skip_reason')
        .eq('purchase_order_id', poId)
        .single();

      if (confError && confError.code !== 'PGRST116') throw confError;

      if (confirmation) {
        setInventoryDeducted(confirmation.inventory_deducted || false);
        setInventoryDeductedAt(confirmation.inventory_deducted_at);
        setInventoryDeductedBy(confirmation.inventory_deducted_by);
        setDeductionNotes(confirmation.inventory_deduction_notes || '');
        setInventorySkipped(confirmation.inventory_skipped || false);
        setInventorySkippedAt(confirmation.inventory_skipped_at);
        setInventorySkippedBy(confirmation.inventory_skipped_by);
        setSkipReason(confirmation.inventory_skip_reason || '');
      }
    } catch (err) {
      console.error('Error loading inventory data:', err);
      setSnackbar({ open: true, message: 'Failed to load inventory data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalUnits = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitType = item.unit_type || 'Case';
    const config = item.unitConfig || { units_per_case: item.units_per_case || 1, cases_per_pallet: item.cases_per_pallet || 1 };
    const upc = config.units_per_case || 1;
    const cpp = config.cases_per_pallet || 1;
    if (unitType === 'Pallet') return Math.round(quantity * cpp * upc);
    if (unitType === 'Case') return Math.round(quantity * upc);
    return Math.round(quantity);
  };

  // Returns formatted qty string for the Qty Ordered column
  const getQtyOrdered = (item) => {
    const qty = Math.round(parseFloat(item.quantity) || 0);
    const unitType = item.unit_type || 'Case';
    if (unitType === 'Unit') return `${qty.toLocaleString()} ${qty === 1 ? 'Unit' : 'Units'}`;
    if (unitType === 'Pallet') return `${qty.toLocaleString()} ${qty === 1 ? 'Pallet' : 'Pallets'}`;
    return `${qty.toLocaleString()} ${qty === 1 ? 'Case' : 'Cases'}`;
  };

  // Format available stock in cases (always cases if unit config supports it)
  const formatAvailable = (item, totalBaseUnits) => {
    const config = item.unitConfig;
    const upc = config?.units_per_case || 1;
    const enableCases = config?.enable_cases !== false && upc > 1;
    if (enableCases) {
      const cases = Math.floor(totalBaseUnits / upc);
      const casesStr = `${cases.toLocaleString()} case${cases !== 1 ? 's' : ''}`;
      return { primary: casesStr, secondary: `${totalBaseUnits.toLocaleString()} units total` };
    }
    return { primary: `${totalBaseUnits.toLocaleString()} units`, secondary: null };
  };

  // Live stock check before showing confirm dialog
  const handleDeductInventory = async () => {
    for (const item of lineItems) {
      if (!item.product_id || !item.location_id) continue;
      const needed = calculateTotalUnits(item);
      let available = 0;

      try {
        if (item.lot_number || item.batch_number) {
          let q = supabase
            .from('product_inventory_items')
            .select('total_base_units')
            .eq('company_id', companyId)
            .eq('product_id', item.product_id)
            .eq('location_id', item.location_id)
            .eq('is_active', true);
          if (item.variant_id) q = q.eq('variant_id', item.variant_id);
          else q = q.is('variant_id', null);
          if (item.lot_number) q = q.eq('lot_number', String(item.lot_number));
          if (item.batch_number) q = q.eq('batch_number', String(item.batch_number));
          const { data } = await q;
          available = (data || []).reduce((sum, l) => sum + (l.total_base_units || 0), 0);
        } else if (item.inventory_item_id) {
          const { data } = await supabase
            .from('product_inventory_items')
            .select('total_base_units')
            .eq('id', item.inventory_item_id)
            .single();
          available = data?.total_base_units || 0;
        } else {
          const { data } = await supabase
            .from('product_inventory')
            .select('total_units')
            .eq('company_id', companyId)
            .eq('product_id', item.product_id)
            .eq('variant_id', item.variant_id || null)
            .eq('location_id', item.location_id)
            .maybeSingle();
          available = data?.total_units || 0;
        }
      } catch { available = 0; }

      if (needed > available) {
        const name = item.product?.name || item.product_name;
        const config = item.unitConfig;
        const upc = config?.units_per_case || 1;
        const enableCases = config?.enable_cases !== false && upc > 1;
        const neededStr = enableCases ? `${Math.ceil(needed / upc)} cases` : `${needed} units`;
        const availableStr = enableCases ? `${Math.floor(available / upc)} cases` : `${available} units`;
        setSnackbar({
          open: true,
          message: `Insufficient stock for ${name}: need ${neededStr}, have ${availableStr}`,
          severity: 'error',
        });
        return;
      }
    }

    if (inventoryDeducted || inventorySkipped) {
      setReconfirmDialogOpen(true);
    } else {
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirmDeduction = async () => {
    setDeducting(true);
    setConfirmDialogOpen(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Group line items by location
      const locationGroups = {};
      for (const item of lineItems) {
        if (!item.product_id || !item.location_id) continue;
        if (!locationGroups[item.location_id]) locationGroups[item.location_id] = [];
        locationGroups[item.location_id].push(item);
      }

      let totalUnitsDeducted = 0;

      for (const [locationId, items] of Object.entries(locationGroups)) {
        const usages = [];

        for (const item of items) {
          const qty = parseFloat(item.quantity) || 0;
          if (qty <= 0) continue;

          // Always do a fresh live query — never use stale inventory_item_id which may
          // point to an inactive/zeroed lot, causing executeUsage to log 0 and wipe it.
          let q = supabase
            .from('product_inventory_items')
            .select('id, total_base_units')
            .eq('company_id', companyId)
            .eq('product_id', item.product_id)
            .eq('location_id', item.location_id)
            .eq('is_active', true)
            .gt('total_base_units', 0);

          if (item.variant_id) q = q.eq('variant_id', item.variant_id);
          else q = q.is('variant_id', null);
          if (item.lot_number)   q = q.eq('lot_number',   String(item.lot_number));
          if (item.batch_number) q = q.eq('batch_number', String(item.batch_number));

          const { data: liveLot } = await q.maybeSingle();
          const lotId = liveLot?.id;

          if (!lotId) {
            const lotInfo = item.lot_number ? ` (Lot ${item.lot_number})` : item.batch_number ? ` (Batch ${item.batch_number})` : '';
            throw new Error(`Cannot find active inventory lot for ${item.product?.name || item.product_name}${lotInfo}. Ensure lot/batch is specified and has stock.`);
          }

          // Pass as pre-computed base units — avoids mismatch with executeUsage's
          // internal units_per_case conversion (which reads from the lot itself).
          const baseUnits = calculateTotalUnits(item);

          usages.push({
            itemId: item.product_id,
            lotId,
            pallets: 0,
            cases:   0,
            units:   baseUnits,
            lot_number:   item.lot_number   || null,
            batch_number: item.batch_number || null,
          });

          totalUnitsDeducted += calculateTotalUnits(item);
        }

        if (usages.length > 0) {
          const piRef = [
            piInfo.piNumber    ? `PI #${piInfo.piNumber}`           : null,
            piInfo.customerName ? `Customer: ${piInfo.customerName}` : null,
            piInfo.companyName  ? `Company: ${piInfo.companyName}`   : null,
          ].filter(Boolean).join(' | ');
          const txNotes = [
            `PI Delivery Deduction`,
            piRef || null,
            deductionNotes || null,
          ].filter(Boolean).join(' — ');

          await usageProductConfig.executeUsage(companyId, {
            locationId,
            usages,
            notes: txNotes,
          });
        }
      }

      // Mark as deducted in po_delivery_confirmations
      const { data: existing } = await supabase
        .from('po_delivery_confirmations')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      const payload = {
        inventory_deducted: true,
        inventory_deducted_at: new Date().toISOString(),
        inventory_deducted_by: user.id,
        inventory_deduction_notes: deductionNotes || `Deducted ${totalUnitsDeducted} total units from inventory`,
        inventory_usage_completed: true,
        inventory_skipped: false,
        inventory_skipped_at: null,
        inventory_skipped_by: null,
        inventory_skip_reason: null,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .update(payload)
          .eq('purchase_order_id', poId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .insert({ company_id: companyId, purchase_order_id: poId, created_by: user.id, ...payload });
        if (error) throw error;
      }

      setInventoryDeducted(true);
      setInventoryDeductedAt(new Date().toISOString());
      setInventoryDeductedBy(user.id);
      setInventorySkipped(false);
      setInventorySkippedAt(null);
      setInventorySkippedBy(null);
      setSkipReason('');

      setSnackbar({ open: true, message: `Inventory deducted successfully! ${totalUnitsDeducted.toLocaleString()} total units removed.`, severity: 'success' });

      await new Promise(resolve => setTimeout(resolve, 500));
      await loadData(true);
      if (onSave) onSave();
    } catch (err) {
      console.error('Error deducting inventory:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setDeducting(false);
    }
  };

  const handleReconfirmDeduction = () => {
    setReconfirmDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const handleSkipInventory = () => setSkipDialogOpen(true);

  const handleConfirmSkip = async () => {
    setSkipDialogOpen(false);
    setDeducting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data: existing } = await supabase
        .from('po_delivery_confirmations')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      const payload = {
        inventory_skipped: true,
        inventory_skipped_at: new Date().toISOString(),
        inventory_skipped_by: user.id,
        inventory_skip_reason: skipReason || 'Inventory deduction skipped',
        inventory_usage_completed: true,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .update(payload)
          .eq('purchase_order_id', poId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .insert({ company_id: companyId, purchase_order_id: poId, created_by: user.id, ...payload });
        if (error) throw error;
      }

      setInventorySkipped(true);
      setInventorySkippedAt(new Date().toISOString());
      setInventorySkippedBy(user.id);
      setSnackbar({ open: true, message: 'Inventory deduction skipped and marked as complete', severity: 'info' });
      if (onSave) onSave();
    } catch (err) {
      console.error('Error skipping inventory:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setDeducting(false);
      setSkipReason('');
    }
  };

  // ─── Access Denied ───────────────────────────────────────────────────────────
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', textAlign: 'center' }}>
          <Card sx={{
            p: 8, borderRadius: 3, border: `1px solid ${theme.palette.grey[400]}`, background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`, maxWidth: 600, width: '100%',
            position: 'relative', overflow: 'hidden',
            '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})` }
          }}>
            <Box sx={{ p: 3, borderRadius: 3, background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`, border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, mb: 6, display: 'inline-flex' }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ mb: 3, fontWeight: 500, color: 'text.primary', fontSize: '2.5rem', letterSpacing: '-0.8px' }}>Access Restricted</Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, fontWeight: 500 }}>Inventory Usage Permissions Required</Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 6, maxWidth: 450, mx: 'auto' }}>
              You don't currently have the necessary permissions to access Inventory Usage.
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 4, py: 1.5 }}>
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  // ─── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Loading inventory data...</Typography>
      </Box>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Card sx={{
      mb: 3, borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: inventoryDeducted
        ? alpha(theme.palette.success.main, 0.02)
        : inventorySkipped
          ? alpha(theme.palette.info.main, 0.02)
          : 'white',
      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}` }
    }}>
      <CardContent sx={{ p: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5, borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
            }}>
              <InventoryIcon sx={{ fontSize: 28, color: 'warning.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'warning.main', mb: 0.5 }}>Inventory Usage</Typography>
              <Typography variant="body2" color="text.secondary">Deduct sold products from inventory</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Refresh inventory levels">
              <IconButton
                onClick={() => loadData(true)}
                disabled={loading}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15), transform: 'rotate(180deg)' },
                  transition: 'all 0.3s ease'
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {inventoryDeducted && (
              <Chip icon={<CheckIcon />} label="INVENTORY DEDUCTED" color="success" sx={{ fontWeight: 500, px: 2, py: 2.5 }} />
            )}
            {inventorySkipped && (
              <Chip icon={<InfoIcon />} label="DEDUCTION SKIPPED" color="info" sx={{ fontWeight: 500, px: 2, py: 2.5 }} />
            )}
          </Box>
        </Box>

        {/* Status alerts */}
        {inventoryDeducted && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.3)}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>✓ Inventory Already Deducted</Typography>
            <Typography variant="body2">
              Deducted on {inventoryDeductedAt ? new Date(inventoryDeductedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : 'Unknown date'}
            </Typography>
            {deductionNotes && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>Notes: {deductionNotes}</Typography>
            )}
          </Alert>
        )}

        {inventorySkipped && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>Inventory Deduction Skipped</Typography>
            <Typography variant="body2">
              Skipped on {inventorySkippedAt ? new Date(inventorySkippedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : 'Unknown date'}
            </Typography>
            {skipReason && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>Reason: {skipReason}</Typography>
            )}
          </Alert>
        )}

        {/* Line Items Table */}
        <Paper sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, overflow: 'hidden', mb: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.grey[100], 0.8) }}>
                  <TableCell sx={{ fontWeight: 500 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 500 }} align="center">UoM</TableCell>
                  <TableCell sx={{ fontWeight: 500 }} align="right">Qty Ordered</TableCell>
                  <TableCell sx={{ fontWeight: 500 }} align="right">Available</TableCell>
                  <TableCell sx={{ fontWeight: 500 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Lot / Batch</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No line items found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  lineItems.map((item, index) => {
                    const totalUnits = calculateTotalUnits(item);
                    const availableUnits = inventoryLevels[item.id] || 0;
                    const hasSufficient = availableUnits >= totalUnits;
                    const { primary: availPrimary, secondary: availSecondary } = formatAvailable(item, availableUnits);
                    const qtyStr = getQtyOrdered(item);

                    return (
                      <TableRow
                        key={item.id}
                        sx={{
                          '&:hover': { bgcolor: alpha(theme.palette.grey[50], 0.5) },
                          bgcolor: index % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.3)
                        }}
                      >
                        {/* Product */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.product?.name || item.product_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {item.variant?.sku || item.product?.sku || item.product_sku || 'N/A'}
                          </Typography>
                        </TableCell>

                        {/* Size (variant) */}
                        <TableCell>
                          {item.variant_name ? (
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'info.main' }}>
                              {item.variant_name}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>

                        {/* Location */}
                        <TableCell>
                          <Typography variant="body2">{item.location?.name || item.location_name || 'N/A'}</Typography>
                        </TableCell>

                        {/* UoM */}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={500}>
                            {item.unitConfig?.units_per_case ? `${item.unitConfig.units_per_case}U/C` : '-'}
                          </Typography>
                        </TableCell>

                        {/* Qty Ordered */}
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {qtyStr}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {totalUnits.toLocaleString()} units total
                          </Typography>
                        </TableCell>

                        {/* Available */}
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500, color: hasSufficient ? 'success.main' : 'error.main' }}>
                            {availPrimary}
                          </Typography>
                          {availSecondary && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {availSecondary}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell align="center">
                          {hasSufficient ? (
                            <Chip label="✓ In Stock" size="small" color="success" variant="outlined" sx={{ fontWeight: 500 }} />
                          ) : (
                            <Chip
                              label={`⚠ Short ${(totalUnits - availableUnits).toLocaleString()} units`}
                              size="small"
                              color="error"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                        </TableCell>

                        {/* Lot / Batch */}
                        <TableCell>
                          <Box>
                            {item.lot_number && (
                              <Typography variant="caption" sx={{ display: 'block' }}>Lot: {item.lot_number}</Typography>
                            )}
                            {item.batch_number && (
                              <Typography variant="caption" sx={{ display: 'block' }}>Batch: {item.batch_number}</Typography>
                            )}
                            {!item.lot_number && !item.batch_number && (
                              <Typography variant="caption" color="warning.main">No Lot/Batch</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Summary */}
        <Paper sx={{
          p: 3, borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          bgcolor: alpha(theme.palette.error.main, 0.05),
          mb: 3
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              {(() => {
                const grandTotalUnits = lineItems.reduce((sum, item) => sum + calculateTotalUnits(item), 0);
                const totalCases = lineItems.reduce((sum, item) => {
                  if (item.unit_type === 'Case') return sum + Math.round(parseFloat(item.quantity) || 0);
                  return sum;
                }, 0);
                const totalLooseUnits = lineItems.reduce((sum, item) => {
                  if (item.unit_type === 'Unit') return sum + Math.round(parseFloat(item.quantity) || 0);
                  return sum;
                }, 0);
                const totalPallets = lineItems.reduce((sum, item) => {
                  if (item.unit_type === 'Pallet') return sum + Math.round(parseFloat(item.quantity) || 0);
                  return sum;
                }, 0);

                // Build primary display: cases first, then pallets, then units
                const parts = [];
                if (totalPallets > 0) parts.push(`${totalPallets.toLocaleString()} ${totalPallets === 1 ? 'Pallet' : 'Pallets'}`);
                if (totalCases > 0) parts.push(`${totalCases.toLocaleString()} ${totalCases === 1 ? 'Case' : 'Cases'}`);
                if (totalLooseUnits > 0) parts.push(`${totalLooseUnits.toLocaleString()} ${totalLooseUnits === 1 ? 'Unit' : 'Units'}`);

                return (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Total to Deduct:</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: 'error.main' }}>
                      {parts.join(' + ') || '0'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {grandTotalUnits.toLocaleString()} units total
                    </Typography>
                  </>
                );
              })()}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Line Items:</Typography>
              <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary' }}>{lineItems.length}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Deduction Notes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>Deduction Notes (Optional)</Typography>
          <TextField
            fullWidth multiline rows={2}
            value={deductionNotes}
            onChange={(e) => canEdit && setDeductionNotes(e.target.value)}
            placeholder="Add any notes about this inventory deduction..."
            disabled={inventoryDeducted || !canEdit}
            sx={{
              opacity: canEdit ? 1 : 0.6,
              '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' }
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ textAlign: 'center' }}>
          {hasInsufficientStock && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, border: `1px solid ${theme.palette.error.main}`, bgcolor: alpha(theme.palette.error.main, 0.08) }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                ⚠ Cannot deduct: Insufficient stock for one or more items
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                Please adjust quantities or add more inventory before deducting
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={deducting ? <CircularProgress size={20} color="inherit" /> : (inventoryDeducted || inventorySkipped) ? <WarningIcon /> : <TrendingDownIcon />}
              onClick={() => canEdit && handleDeductInventory()}
              disabled={lineItems.length === 0 || !canEdit || hasInsufficientStock || deducting}
              sx={{
                px: 6, py: 2, borderRadius: 3, fontWeight: 500, fontSize: '1rem',
                background: hasInsufficientStock
                  ? theme.palette.grey[400]
                  : (inventoryDeducted || inventorySkipped)
                    ? theme.palette.warning.main
                    : theme.palette.error.main,
                color: 'white',
                boxShadow: hasInsufficientStock ? 'none' : `0 6px 18px ${alpha((inventoryDeducted || inventorySkipped) ? theme.palette.warning.main : theme.palette.error.main, 0.3)}`,
                '&:hover': {
                  background: hasInsufficientStock
                    ? theme.palette.grey[400]
                    : (inventoryDeducted || inventorySkipped) ? theme.palette.warning.dark : theme.palette.error.dark,
                  transform: hasInsufficientStock ? 'none' : 'translateY(-2px)',
                },
                '&.Mui-disabled': { opacity: 0.6, color: 'white', background: theme.palette.grey[500] }
              }}
            >
              {deducting
                ? 'Deducting...'
                : inventoryDeducted
                  ? 'Deduct Inventory Again (Re-process)'
                  : inventorySkipped
                    ? 'Deduct Inventory Now'
                    : 'Deduct Inventory from Stock'}
            </Button>

            {!inventoryDeducted && !inventorySkipped && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<InfoIcon />}
                onClick={() => canEdit && handleSkipInventory()}
                disabled={lineItems.length === 0 || !canEdit}
                sx={{
                  px: 4, py: 2, borderRadius: 3, fontWeight: 500, fontSize: '1rem',
                  borderColor: theme.palette.info.main, color: theme.palette.info.main,
                  '&:hover': { borderColor: theme.palette.info.dark, bgcolor: alpha(theme.palette.info.main, 0.05), transform: 'translateY(-2px)' },
                  '&.Mui-disabled': { opacity: 0.3 }
                }}
              >
                Skip Deduction & Mark Complete
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>

      {/* ── Confirm Dialog ──────────────────────────────────────────────────────── */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` } }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          fontWeight: 500, color: 'error.main',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon />
            Confirm Inventory Deduction
          </Box>
          <IconButton onClick={() => setConfirmDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>This will permanently deduct inventory</Typography>
            <Typography variant="body2">
              The following products will be deducted from their specified lot/batch inventory.
            </Typography>
          </Alert>
          <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, bgcolor: alpha(theme.palette.error.main, 0.03) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'error.main' }}>Items to Deduct:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {lineItems.map((item) => {
                const totalUnits = calculateTotalUnits(item);
                const config = item.unitConfig;
                const upc = config?.units_per_case || 1;
                const enableCases = config?.enable_cases !== false && upc > 1;
                const caseCount = Math.ceil(totalUnits / upc);
                const chipLabel = enableCases
                  ? `−${caseCount} case${caseCount !== 1 ? 's' : ''} (${totalUnits.toLocaleString()} units)`
                  : `−${totalUnits.toLocaleString()} units`;
                return (
                  <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 1, bgcolor: 'white', border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.product?.name || item.product_name}
                        {item.variant_name ? ` — ${item.variant_name}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.location?.name || item.location_name}
                        {item.lot_number ? ` · Lot ${item.lot_number}` : ''}
                        {item.batch_number ? ` · Batch ${item.batch_number}` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label={chipLabel}
                      size="small"
                      color="error"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeduction}
            disabled={deducting}
            startIcon={deducting ? <CircularProgress size={16} color="inherit" /> : <TrendingDownIcon />}
            sx={{ borderRadius: 2, px: 4, fontWeight: 500 }}
          >
            {deducting ? 'Deducting...' : 'Confirm Deduction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reconfirm Dialog ────────────────────────────────────────────────────── */}
      <Dialog
        open={reconfirmDialogOpen}
        onClose={() => setReconfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}` } }}
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon />
            Already Processed — Deduct Again?
          </Box>
          <IconButton onClick={() => setReconfirmDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {inventoryDeducted ? 'Inventory was already deducted for this order.' : 'This order was previously marked as skipped.'}
            </Typography>
            <Typography variant="body2">
              Re-processing will deduct inventory again. Only do this if the previous deduction was incorrect.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setReconfirmDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleReconfirmDeduction} sx={{ borderRadius: 2, px: 4, fontWeight: 500 }}>
            Deduct Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Skip Dialog ─────────────────────────────────────────────────────────── */}
      <Dialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <InfoIcon color="info" />
            Skip Inventory Deduction
          </Box>
          <IconButton onClick={() => setSkipDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Skipping will mark this step as complete without actually deducting from inventory.
          </Typography>
          <TextField
            fullWidth multiline rows={3}
            label="Reason for skipping (Optional)"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder="e.g. Inventory managed externally, drop-ship order, etc."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setSkipDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" color="info" onClick={handleConfirmSkip} sx={{ borderRadius: 2, px: 4, fontWeight: 500 }}>
            Skip & Mark Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default InventoryUsageSection;
